import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import { unwrapData } from '../../../core/services/unwrap-data';
import { environment } from '../../../../environments/environment';

export enum KeyManagerProvider {
    OPENAI = 'OPENAI',
    ANTHROPIC = 'ANTHROPIC',
    GOOGLE_AI = 'GOOGLE_AI',
    AZURE_OPENAI = 'AZURE_OPENAI',
    MISTRAL = 'MISTRAL',
    COHERE = 'COHERE',
    CUSTOM = 'CUSTOM',
}

export enum KeyManagerStatus {
    ACTIVE = 'ACTIVE',
    DISABLED = 'DISABLED',
    REVOKED = 'REVOKED',
    EXPIRED = 'EXPIRED',
    ROTATED_OUT = 'ROTATED_OUT',
}

export interface CreateKeyPayload {
    name: string;
    provider: KeyManagerProvider;
    purpose: string;
    apiKey: string;
    productId: number;
    status?: KeyManagerStatus;
    metadata?: Record<string, unknown> | null;
}

/** Public row shape returned from list/get/update (no accessToken). */
export interface Keys {
    pid: number;
    id: string;
    name: string;
    provider: KeyManagerProvider;
    status: KeyManagerStatus;
    purpose: string;
    displayPrefix: string;
    accessCount: number;
    createdByUserPid: number | null;
    /** Display name from creator user (first + last). */
    createdByUserName: string | null;
    productId: number;
    productName?: string;
    metadata: unknown;
    createdAt: string;
    updatedAt: string;
    lastRotatedAt: string | null;
}

export interface Product {
    pid: number;
    id: string;
    name: string;
}

export interface UpdateKeyPayload {
    name?: string;
    status?: KeyManagerStatus;
    purpose?: string;
    apiKey?: string;
}

/** Backend returns this once from POST /key-manager/create */
export interface CreateKeyResponse {
    pid: number;
    id: string;
    name: string;
    /** Plaintext; store only where appropriate — never log. */
    accessToken: string;
}

/** Backend returns this from POST .../regenerate-access-token */
export interface RegenerateAccessTokenResponse {
    accessToken: string;
}

/** GET .../original-key body: prism-new sends `apiKey`, the old API sent `originalKey`. */
export interface OriginalKeyResponse {
    apiKey?: string;
    originalKey?: string;
}

/**
 * Builds the public open URL (no JWT). Caller supplies API base (e.g. environment.apiUrl).
 * Backend: GET /key-manager/open/:id?accessToken=...
 */
export function buildKeyManagerOpenUrl(apiBaseUrl: string, keyId: string, accessToken: string): string {
    const base = apiBaseUrl.replace(/\/+$/, '');
    const q = new URLSearchParams({ accessToken });
    return `${base}/api/v1/key-manager/open/${encodeURIComponent(keyId)}?${q.toString()}`;
}

@Injectable({
    providedIn: 'root',
})
export class KeyManagerService {
    private api = inject(ApiService);
    private readonly basePath = 'key-manager';
    private currentAdminDetails = signal<any | null>(null);

    private path(segment: string): string {
        return `/${this.basePath}/${segment}`;
    }

    /** GET /key-manager/list?status=&productId=&page=&limit= */
    getKeys(filters?: { status?: KeyManagerStatus; productId?: number; page?: number; limit?: number }): Observable<any> {
        return this.api.get<any>(this.path('list'), filters);
    }

    /** GET /product/list-product */
    getProducts(): Observable<Product[]> {
        return this.api.get<Product[]>('/product/list-product').pipe(map((raw) => unwrapData<Product[]>(raw)));
    }

    /** GET /user/list-by-product?productId= */
    getUsersByProduct(productId: number): Observable<any[]> {
        return this.api.get<any[]>('/user/list-by-product', { productId }).pipe(map((raw) => unwrapData<any[]>(raw)));
    }

    /** GET /key-manager/pid/:pid → { key } */
    getKeyByPid(pid: number): Observable<Keys> {
        return this.api.get<{ key: Keys }>(this.path(`pid/${pid}`)).pipe(
            map((raw) => unwrapData<{ key: Keys }>(raw)),
            map((body) => body.key),
        );
    }

    /** GET /key-manager/:id → { key } */
    getKeyById(id: string): Observable<Keys> {
        return this.api.get<{ key: Keys }>(this.path(id)).pipe(
            map((raw) => unwrapData<{ key: Keys }>(raw)),
            map((body) => body.key),
        );
    }

    /** POST /key-manager/create → { pid, id, name, accessToken } */
    createKey(payload: CreateKeyPayload): Observable<CreateKeyResponse> {
        const body: CreateKeyPayload = {
            ...payload,
            status: payload.status ?? KeyManagerStatus.ACTIVE,
        };
        return this.api.post<CreateKeyResponse>(this.path('create'), body).pipe(map((raw) => unwrapData<CreateKeyResponse>(raw)));
    }

    /** PUT /key-manager/pid/:pid → { key } */
    updateKey(pid: number, payload: UpdateKeyPayload): Observable<Keys> {
        return this.api.put<{ key: Keys }>(this.path(`pid/${pid}`), payload).pipe(
            map((raw) => unwrapData<{ key: Keys }>(raw)),
            map((b) => b.key),
        );
    }

    /** POST /key-manager/pid/:pid/revoke → { key } */
    revokeKey(pid: number): Observable<Keys> {
        return this.api.post<{ key: Keys }>(this.path(`pid/${pid}/revoke`)).pipe(
            map((raw) => unwrapData<{ key: Keys }>(raw)),
            map((b) => b.key),
        );
    }

    /** POST /key-manager/pid/:pid/rotate → { key } */
    rotateKey(pid: number, newApiKey: string): Observable<Keys> {
        return this.api.post<{ key: Keys }>(this.path(`pid/${pid}/rotate`), { apiKey: newApiKey }).pipe(
            map((raw) => unwrapData<{ key: Keys }>(raw)),
            map((b) => b.key),
        );
    }

    /** POST /key-manager/pid/:pid/regenerate-access-token → { accessToken } */
    regenerateAccessToken(pid: number): Observable<RegenerateAccessTokenResponse> {
        return this.api
            .post<RegenerateAccessTokenResponse>(this.path(`pid/${pid}/regenerate-access-token`))
            .pipe(map((raw) => unwrapData<RegenerateAccessTokenResponse>(raw)));
    }

    /** GET /key-manager/pid/:pid/original-key (JWT) → { apiKey } (old API: { originalKey }); '' when absent. */
    originalKey(pid: number): Observable<string> {
        return this.api.get<OriginalKeyResponse>(this.path(`pid/${pid}/original-key`)).pipe(
            map((raw) => unwrapData<OriginalKeyResponse>(raw)),
            map((b) => b?.apiKey ?? b?.originalKey ?? ''),
        );
    }

    /** POST /key-manager/access/bulk */
    bulkUpdateAccess(payload: any): Observable<any> {
        return this.api.post<any>(this.path('access/bulk'), payload);
    }

    /**
     * GET /key-manager/pid/:pid/access-url → { url } (full shareable URL from backend).
     */
    buildPublicAccessUrl(pid: number): Observable<string> {
        return this.api.get<{ url?: string; accessUrl?: string }>(this.path(`pid/${pid}/access-url`)).pipe(
            map((raw) => unwrapData<{ url?: string; accessUrl?: string }>(raw)),
            map((b) => (typeof b?.url === 'string' ? b.url : typeof b?.accessUrl === 'string' ? b.accessUrl : '')),
        );
    }
}